// Shared container for the Adyen card-add (tokenization) flow — CPS-26589,
// Milestone B, Task 7 (part 1). This is the single place that OWNS the flow:
// it wires the platform-agnostic orchestration hook (useAdyenCardAdd) to the
// activation poller (useAdyenCardPolling), merges their states into the one
// AdyenFlowStatus the sheet consumes, and renders the platform-split
// <AdyenCardAddSheet> (which forks itself web/native — so this file needs no
// .web split).
//
// It deliberately does NOT initiate the flow: T6 (useAddPaymentMethod) fires
// ADYEN_INIT_CARD_ADD and stashes {cardId, transactionId} in the
// adyenCardAddSession reactive var BEFORE the render site mounts this. The
// container reads those ids back; if the session is absent it renders null.
//
// The two render sites (modal + onboarding — a SEPARATE task, part 2) mount this
// with just {onSuccess, onClose}. Everything else (SDK context, cache refresh on
// activation, restart-with-fresh-init, abandon cancel, session-lifecycle
// cleanup) lives here.
//
// STRUCTURE (outer + key-remounted inner): the outer reads the session and owns
// its lifecycle; the inner (AdyenCardAddFlow) runs the discovery/poll machine.
// The inner is keyed on session.transactionId, so restart()'s fresh
// adyenInitCardAdd (which writes new ids into the session) changes the key and
// React REMOUNTS the inner — re-running mount discovery with the new ids. That
// remount is the entire mechanism by which a refused/error user gets a fresh
// card form rather than being stuck on the terminal screen.

import { useApolloClient, useMutation, useReactiveVar } from '@apollo/client';
import { GET_CUSTOMER } from '@queries/getCustomer';
import { Spinner } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import { ADYEN_CANCEL_CARD_ADD } from 'graphql/mutations/adyenCancelCardAdd';
import { ADYEN_INIT_CARD_ADD } from 'graphql/mutations/adyenInitCardAdd';
import i18next from 'i18next';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Platform } from 'react-native';
import { country, featureCountryCode } from 'states/persistInStorage';
import { AdyenCardAddSession, usePaymentStore } from 'stores/paymentStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  Card,
  Mutation,
  MutationAdyenCancelCardAddArgs,
  MutationAdyenInitCardAddArgs,
} from 'types/generatedSchemaTypes';
import { checkoutChannelForPlatform } from 'utils/adyenChannel';
import { useAdyenCardAdd } from 'hooks/useAdyenCardAdd';
import type { AdyenCardAddStatus } from 'hooks/useAdyenCardAdd';
import { useAdyenCardPolling } from 'hooks/useAdyenCardPolling';
import type { AdyenCardPollingPhase } from 'hooks/useAdyenCardPolling';
import { trackAdyenCardAdd } from 'utils/adyenAnalytics';
import {
  clearAdyenRedirectSession,
  persistAdyenRedirectSession,
} from 'utils/adyenRedirectSession';
import { getWalletPaymentMethodLabelKey } from 'utils/paymentUtils';
import { refreshCustomerBestEffort } from 'utils/refreshCustomerBestEffort';
import { getRedirectUrl } from 'utils/urlUtils';

import AdyenCardAddSheet from './AdyenCardAddSheet';
import type {
  AdyenFlowStatus,
  AdyenPaymentResult,
  AdyenStateData,
} from './adyenCardAddSheet.types';

export type AdyenCardAddContainerProps = {
  onSuccess: () => void;
  onClose: () => void;
};

// Props the outer hands the keyed inner: the whole session (one value, not
// re-spread fields) plus the terminal handoffs. session.transactionId
// additionally drives the inner's `key`. session.walletType (CPS-26590 Task 5)
// is absent for a plain card add. The session shape stops here: downstream
// consumers (useAdyenCardAdd input, the polling hook, the persisted redirect
// session) each take individual fields mixed with non-session data and have
// their own shapes.
type AdyenCardAddFlowProps = AdyenCardAddContainerProps & {
  session: AdyenCardAddSession;
};

// Merge the two state sources into the single status the sheet renders. The
// poller wins once activation begins: `active` -> done, `timedOut` -> timedOut;
// otherwise the in-sheet flow status drives. The flow union spells the timeout
// member `timeout` (reserved for the polling layer, which the hook never sets)
// while the sheet union spells it `timedOut`; normalize so the timeout source is
// unambiguously the poller.
const mergedStatus = (
  pollingPhase: AdyenCardPollingPhase,
  flowStatus: AdyenCardAddStatus
): AdyenFlowStatus => {
  if (pollingPhase === 'active') {
    return 'done';
  }
  if (pollingPhase === 'timedOut') {
    return 'timedOut';
  }
  if (flowStatus === 'timeout') {
    return 'timedOut';
  }
  return flowStatus;
};

// Statuses from which closing the sheet abandons a never-activated card, so
// the backend row is discarded (adyenCancelCardAdd). Excluded on purpose:
// `submitting` (/payments in flight — a cancel would orphan a token at Adyen),
// `activating`/`timedOut` (the user submitted successfully and a live token is
// being activated; closing early must not delete it), `done`, and `refused`
// (the backend already discarded the row).
const ABANDONABLE_STATUSES: ReadonlySet<AdyenFlowStatus> =
  new Set<AdyenFlowStatus>([
    'methods',
    'ready',
    'action',
    'error',
    'cancelled',
  ]);

// The backend row is gone or being discarded in all three: `refused` = the
// backend discarded it, `cancelled` / `error` = the abandon cancel was sent.
const SLOT_RELEASING_STATUSES: ReadonlySet<AdyenFlowStatus> =
  new Set<AdyenFlowStatus>(['refused', 'cancelled', 'error']);

// Mirrors useAdyenCardAdd's applyResult: only these hand off to activation
// with a live token; every other outcome leaves no token behind.
const leadsToActivation = (result: AdyenPaymentResult): boolean => {
  const code = result.resultCode?.toUpperCase();
  return code === 'AUTHORISED' || code === 'PENDING';
};

type AdyenSubmitHandler = (
  state: AdyenStateData
) => Promise<AdyenPaymentResult>;

// The inner flow. Mounted by the outer with the current session ids, keyed on
// transactionId. Holds the full discovery/poll/merge machine, the eager
// redirect-session persist, the analytics, restart-with-fresh-init, the terminal
// onSuccess/onClose handoff, the bootstrap-gating spinner and the sheet render.
//
// It intentionally does NOT clear adyenCardAddSession on its unmount: a restart()
// remounts this inner (key change), and a clear here would wipe the just-written
// fresh ids before the new inner reads them. Session-lifecycle cleanup is the
// OUTER's job (it clears on the modal/route actually closing).
const AdyenCardAddFlow = ({
  session,
  onSuccess,
  onClose,
}: AdyenCardAddFlowProps): React.JSX.Element | null => {
  const { cardId, transactionId, walletType } = session;
  // Key the Adyen path's countryCode on featureCountryCode — the SAME source the
  // flag gate (isAdyenCardAddEnabledForCountry) and the entry-hook init key on —
  // so discovery/create/submit run against the gated market and routing agrees
  // with the merchant init (no-fallback safety). The gate returns false for a
  // null app country, so reaching this Adyen flow guarantees it is non-null;
  // `?? country` is a defensive fallback only. (The Nets path stays on `country`.)
  const appCountry = useReactiveVar(featureCountryCode);
  const countryVar = useReactiveVar(country);
  const countryCode = appCountry ?? countryVar;
  const client = useApolloClient();

  // Fresh init per restart (decision 8): re-initiate ADYEN_INIT_CARD_ADD and
  // stash the new ids so the outer re-keys and re-mounts this flow from
  // `methods`. A spent transaction is never reused. Mirrors the T6 entry hook's
  // variables/onError (and keys countryCode on featureCountryCode, as above).
  // walletType (absent for a plain card add) rides along so a wallet retry
  // re-inits a WALLET transaction rather than silently degrading to a plain
  // card add — same conditional-spread-when-present pattern useAddPaymentMethod
  // uses for the entry-point init.
  const [adyenInitCardAdd] = useMutation<
    Mutation,
    MutationAdyenInitCardAddArgs
  >(ADYEN_INIT_CARD_ADD, {
    // Customer refresh is best-effort AFTER a successful restart init (see
    // refreshCustomerBestEffort) — not via awaitRefetchQueries, which would
    // reject the mutation and leave the old session when GET_CUSTOMER fails.
    variables: {
      input: {
        countryCode,
        language: i18next.language || 'FI',
        returnUrl: getRedirectUrl('adyen-redirect'),
        // True surface of this restart, persisted server-side as wallet
        // consent evidence - same value the Checkout calls below report.
        checkoutChannel: checkoutChannelForPlatform(),
        ...(walletType ? { walletType } : {}),
      },
    },
  });

  // On webhook-confirmed activation: refresh the customer (which carries the
  // card list / paymentOptions — there is no standalone readCards query in this
  // app, GET_CUSTOMER is the canonical card-list source, same query the
  // add/remove card mutations refetch) so the newly-Active card appears. Then
  // populate selectedPaymentCard with the just-activated card so the success
  // modal (setSheetModal('paymentCard')) renders the real card — matching the
  // shape and timing the Nets CreditCardForm uses when its readCard poll reaches
  // Active. The success handoff itself (onSuccess + session clear) is driven
  // once by the terminal effect below off the merged `done` status, NOT here —
  // so onSuccess is never fired twice for one activation.
  const handleActivated = useCallback(
    (card: Card) => {
      // Webhook-confirmed activation: the terminal success of the funnel. `card`
      // is exactly what the poller's readCard(cardId) just returned, so we
      // populate the success modal straight from it — no whole-customer
      // round-trip (which previously raced/missed and left selectedPaymentCard's
      // cardId empty, breaking the post-add rename with CARD_NOT_FOUND).
      trackAdyenCardAdd('active');
      // The cardId is the session id we polled with: READ_CARD doesn't select
      // cardId, but it IS the id readCard succeeded on, so the rename /
      // set-default actions target the real card.
      //
      // A card just added through this flow is never the default at creation —
      // the user sets the default via the PaymentCardContent toggle afterwards
      // (same as the Nets path for a new card). Card.isDefault is deprecated and
      // there is no per-card non-deprecated replacement (the non-deprecated
      // isDefaultTokenizedPaymentOption lives on PaymentOption, not Card), so we
      // don't read it here.
      //
      // A stored-wallet card (Apple Pay/Google Pay) has no card.type (it's
      // typically null for these tokens) and no meaningful PAN to mask — mirror
      // CardItem's wallet-row treatment (getWalletPaymentMethodLabelKey for the
      // title, no PAN-style subtitle) instead of falling through to the
      // plain-card projection, which would otherwise leave the post-add modal's
      // headline blank and show a "**** 1234" subtitle CardItem deliberately
      // suppresses for wallet rows.
      const walletLabelKey = getWalletPaymentMethodLabelKey(card.paymentMethod);
      const panSubtitle = card.lastNums ? ` **** ${card.lastNums}` : '';
      usePaymentStore.getState().setSelectedPaymentCard({
        cardId,
        title: walletLabelKey ? i18next.t(walletLabelKey) : (card.type ?? ''),
        subtitle: walletLabelKey ? '' : panSubtitle,
        category: '',
        status: '',
        isDefault: false,
        description: '',
        newCard: true,
        expMonth: card.expMonth ?? '',
        expYear: card.expYear ?? '',
      });
      // Refresh the cached card list ONCE (not a poll) so the newly-Active card
      // appears when the user returns to the payment-methods screen. Fire-and-
      // forget against the already-observed GET_CUSTOMER; the success path never
      // blocks on it.
      client.refetchQueries({ include: [GET_CUSTOMER] });
    },
    [client, cardId]
  );

  const flow = useAdyenCardAdd({
    cardId,
    transactionId,
    countryCode,
    checkoutChannel: checkoutChannelForPlatform(),
    locale: i18next.language || 'FI',
    returnUrl: getRedirectUrl('adyen-redirect'),
    // Web-only: adyen-web needs the initiating page origin for inline 3DS2
    // device fingerprinting. Native ignores it.
    origin: Platform.OS === 'web' ? globalThis.location?.origin : undefined,
    // Absent for a plain card add (unfiltered discovery, componentKind
    // 'card'); set for a stored-wallet add so the hook filters discovery to
    // the matching entry and exposes the componentKind the platform sheet
    // (Tasks 5-7) mounts.
    walletType,
  });

  const polling = useAdyenCardPolling({
    cardId,
    enabled: flow.status === 'activating',
    onActivated: handleActivated,
  });

  // Funnel: the activation poll budget elapsed without the card going Active.
  // Fires once on the idle/polling -> timedOut edge (the poller's settled guard
  // makes timedOut terminal, so this never re-emits).
  useEffect(() => {
    if (polling.phase === 'timedOut') {
      trackAdyenCardAdd('timeout');
    }
  }, [polling.phase]);

  // See mergedStatus (module scope): the poller wins once activation begins,
  // otherwise the in-sheet flow status drives.
  const status: AdyenFlowStatus = mergedStatus(polling.phase, flow.status);
  const statusRef = useRef<AdyenFlowStatus>(status);
  statusRef.current = status;

  // Discard the never-activated card server-side. Fire-and-forget and at most
  // once per attempt (this inner is keyed on transactionId, so a restart gets
  // a fresh guard). Every failure is swallowed: the BFF is idempotent for an
  // already-removed row, and a 409 means the card activated meanwhile — in
  // both cases there is nothing for the user to act on.
  const cancelSentRef = useRef(false);
  const cancelCardAdd = useCallback(() => {
    if (cancelSentRef.current) {
      return;
    }
    cancelSentRef.current = true;
    // Nothing here fires AT page unload (React runs no cleanups then).
    // keepalive protects an already-started cancel — the user often closes
    // the tab or navigates away right after dismissing the sheet, and without
    // it the browser aborts the in-flight request. Native fetch ignores it.
    client
      .mutate<Mutation, MutationAdyenCancelCardAddArgs>({
        mutation: ADYEN_CANCEL_CARD_ADD,
        variables: { input: { cardId, transactionId } },
        context: { fetchOptions: { keepalive: true } },
      })
      .catch(() => undefined);
  }, [client, cardId, transactionId]);

  // Wallet-slot release, at most once per attempt. Three paths reach it (the
  // terminal effect, the unmount abandon, a /payments settling after unmount)
  // and inline render-site props re-run the terminal effect on every render.
  const slotReleasedRef = useRef(false);
  const releaseWalletSlot = useCallback((): boolean => {
    if (!walletType || slotReleasedRef.current) {
      return false;
    }
    slotReleasedRef.current = true;
    usePaymentStore.getState().clearAdyenWalletSlot(walletType);
    return true;
  }, [walletType]);

  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  // A /payments settling after the sheet closed mid-`submitting` never reaches
  // the terminal effect, so the wallet marker would stay occupied for good. No
  // cancel here: refused = backend discarded; error = next initiate discards.
  const releasingWalletSlotAfterUnmount = useCallback(
    (handler: AdyenSubmitHandler): AdyenSubmitHandler =>
      async (state) => {
        try {
          const result = await handler(state);
          if (unmountedRef.current && !leadsToActivation(result)) {
            releaseWalletSlot();
          }
          return result;
        } catch (error) {
          if (unmountedRef.current) {
            releaseWalletSlot();
          }
          throw error;
        }
      },
    [releaseWalletSlot]
  );

  const onSubmit = useMemo(
    () => releasingWalletSlotAfterUnmount(flow.onSubmit),
    [releasingWalletSlotAfterUnmount, flow.onSubmit]
  );
  const onAdditionalDetails = useMemo(
    () => releasingWalletSlotAfterUnmount(flow.onAdditionalDetails),
    [releasingWalletSlotAfterUnmount, flow.onAdditionalDetails]
  );

  // WEB redirect-resume persistence (decision 7, Task 8). A web RedirectShopper
  // 3DS2 action navigates the whole page away and back to /adyen-redirect,
  // losing this in-memory reactive var. So once bootstrap resolves (it carries
  // the checkoutAttemptId — the third id the resume submit needs alongside
  // cardId/transactionId) we eagerly stash the three ids in sessionStorage
  // BEFORE any redirect can fire. Stashing here (not in the web sheet's
  // handleResult) means the ids are already durable the instant the SDK decides
  // to redirect; the sheet needs no persist of its own. Native is a no-op (3DS2
  // returns in-app, JS context survives).
  //
  // walletType (CPS-26590 Task 7) rides along too — this flow already has it
  // (destructured above, straight off the session), and dropping it here would
  // silently make the persisted redirect session lossier than the in-memory one
  // it's stashed from (see adyenRedirectSession.types.ts).
  const checkoutAttemptId = flow.bootstrap?.checkoutAttemptId;
  useEffect(() => {
    if (cardId && transactionId && checkoutAttemptId) {
      persistAdyenRedirectSession({
        cardId,
        transactionId,
        checkoutAttemptId,
        walletType,
      });
    }
  }, [cardId, transactionId, checkoutAttemptId, walletType]);

  // Restart from a terminal refused/error (onRetry): re-initiate with FRESH ids.
  // Writing the new session re-keys the outer's inner -> React remounts this flow
  // and useAdyenCardAdd re-discovers from `methods`. On init error, surface the
  // same retryable toast T6 uses and leave the session as-is.
  const restart = useCallback(async () => {
    try {
      const { data } = await adyenInitCardAdd();
      const next = data?.adyenInitCardAdd;
      if (next?.cardId && next?.transactionId) {
        usePaymentStore.getState().setAdyenCardAddSession({
          cardId: next.cardId,
          transactionId: next.transactionId,
          // Carry walletType forward so a retried wallet add stays a wallet
          // add on the fresh transaction — omitted for a plain card add, same
          // as the session shape the entry hook (useAddPaymentMethod) writes.
          ...(walletType ? { walletType } : {}),
        });
        if (walletType) {
          usePaymentStore.getState().occupyAdyenWalletSlot(walletType);
        }
        // Fire-and-forget: ADDING stubs are not in Customer.paymentCards today.
        void refreshCustomerBestEffort(client);
      } else {
        showToast('error.adyenCardAddInit', 'error');
      }
    } catch {
      showToast('error.adyenCardAddInit', 'error');
    }
  }, [adyenInitCardAdd, client, walletType]);

  // Terminal handoff: `done` -> onSuccess (handleActivated refreshed the cache on
  // the `active` path just before this fires). `cancelled` -> onClose. The modal
  // closing then unmounts the OUTER, which clears the session — so no session
  // clear is needed here (and must not live here: it would fire on a restart
  // remount too and wipe the fresh ids).
  //
  // `cancelled` / `error` discard the never-activated row via cancelCardAdd;
  // `refused` does not (payment-service discards it in handleRefusedPayment
  // before the GraphQL response returns). In all three the backend slot is
  // free, so clear the local wallet marker and best-effort-refresh
  // GET_CUSTOMER so the cached card list is not stuck on a stale view. Retry
  // re-occupies in restart() above. `timedOut` keeps the marker: a live token
  // is still activating server-side.
  useEffect(() => {
    if (status === 'done') {
      onSuccess();
      return;
    }
    if (status === 'cancelled' || status === 'error') {
      cancelCardAdd();
    }
    if (SLOT_RELEASING_STATUSES.has(status) && releaseWalletSlot()) {
      void refreshCustomerBestEffort(client);
    }
    if (status === 'cancelled') {
      onClose();
    }
  }, [status, onSuccess, onClose, client, cancelCardAdd, releaseWalletSlot]);

  // Abandon on unmount (sheet closed, modal dismissed, route left) while the
  // card was never submitted. Deferred one tick: React StrictMode simulates an
  // unmount and immediately remounts, and the remount's setup disarms the
  // pending cancel — an undeferred cancel would discard every card-add on the
  // first render of a development build.
  const pendingAbandonRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  useEffect(() => {
    clearTimeout(pendingAbandonRef.current);
    return () => {
      pendingAbandonRef.current = setTimeout(() => {
        if (
          cancelSentRef.current ||
          !ABANDONABLE_STATUSES.has(statusRef.current)
        ) {
          return;
        }
        cancelCardAdd();
        releaseWalletSlot();
      }, 0);
    };
  }, [cancelCardAdd, releaseWalletSlot]);

  // Bootstrap gating: until /paymentMethods resolves there is no clientKey /
  // paymentMethods to hand the SDK, so the sheet cannot mount. Show a spinner
  // rather than a half-configured sheet.
  if (!flow.bootstrap) {
    // Both platforms now render the flow inside their modal (web modal / native
    // SheetModal) with the embedded card form, so an inline spinner is the right
    // placeholder on both: it shows in the open sheet while discovery completes,
    // then the embedded card form replaces it once bootstrap resolves.
    return (
      <StyledWrapper center testID="adyen-card-add-loading">
        <Spinner />
      </StyledWrapper>
    );
  }

  return (
    <AdyenCardAddSheet
      status={status}
      clientKey={flow.bootstrap.clientKey}
      environment={flow.bootstrap.environment}
      countryCode={countryCode}
      paymentMethods={flow.bootstrap.paymentMethods}
      refusalReasonCategory={flow.refusalReasonCategory}
      returnUrl={getRedirectUrl('adyen-redirect')}
      onSubmit={onSubmit}
      onAdditionalDetails={onAdditionalDetails}
      onResult={flow.onResult}
      onError={flow.onError}
      onValidateMerchant={flow.onValidateMerchant}
      onRetry={restart}
      onClose={onClose}
      componentKind={flow.componentKind}
    />
  );
};

const AdyenCardAddContainer = ({
  onSuccess,
  onClose,
}: AdyenCardAddContainerProps): React.JSX.Element | null => {
  const session = usePaymentStore((s) => s.adyenCardAddSession);

  // Do NOT clear adyenCardAddSession on unmount. It is set by the entry hook
  // (useAddPaymentMethod) BEFORE this mounts and is NOT re-set on a remount, so
  // wiping it on unmount is fatal under React StrictMode (index.web.tsx): its dev
  // mount->unmount->remount cycle would clear the ids before the real mount reads
  // them, leaving the web sheet empty. A lingering session is harmless — every
  // open re-runs adyenInitCardAdd and overwrites it, and it is in-memory (gone on
  // reload). Only the redirect session (re-persisted by the flow once bootstrap
  // resolves) is cleared here.
  useEffect(() => {
    return () => {
      clearAdyenRedirectSession();
    };
  }, []);

  // T6 stashes the session before this mounts; with no ids there is nothing to
  // render.
  if (!session?.cardId || !session?.transactionId) {
    return null;
  }

  // Key on transactionId: restart()'s fresh init writes new ids into the session,
  // changing the key -> React remounts AdyenCardAddFlow -> useAdyenCardAdd mounts
  // fresh and re-runs discovery with the new ids. That remount is the fix for the
  // "stuck on the refused screen" bug.
  return (
    <AdyenCardAddFlow
      key={session.transactionId}
      session={session}
      onSuccess={onSuccess}
      onClose={onClose}
    />
  );
};

export default AdyenCardAddContainer;
